// The browse view's two tabs, as data: the Datasets tab's topic list with the
// maps placed in it, and the Maps tab's cards. Pure, DOM- and state-free;
// main.ts renders what these return.
//
// The maps come from GET /maps (maps.dataseries.org's maps.json). A map without
// `series` is a map-only entry: it sits in its topic like a dataset, under its
// own `concept` subhead, so it joins the datasets of the same subject. A map
// with `series` shows the same statistic as those datasets; it gets no row of
// its own there, only a Map pill on theirs. The Maps tab lists every map.
//
// `concept` and `thumbnail` arrived with maps.json 1.1. On an older list a
// map-only entry falls back to a catch-all "Maps" subhead (MAPS_LEAF), and a
// card without a picture shows a placeholder.
import { label, srcOf } from "./meta";
import { freqLabel } from "./transforms";
import { t, tn } from "./locale";
import type { CatalogItem, MapItem } from "./search";

// ---- catalog grouping ---------------------------------------------------------

/** A concept split into [group, leaf]. `concept` is a bare "Group / Leaf"
 * string, or {en, leaf:{i18n}} in schema 1.1. The group is only an identity
 * (grouping keys off topic.key); the leaf is displayed, so it resolves through
 * label() when the object form is present. */
export function splitConcept(cRaw: unknown): [string, string] {
  const o = cRaw && typeof cRaw === "object" && !Array.isArray(cRaw) ? cRaw as Record<string, any> : null;
  const c = label(o ? o.en : cRaw as string);
  const i = (c || "").indexOf(" / ");
  const leaf = o && o.leaf ? label(o.leaf) : (i < 0 ? "" : c.slice(i + 3));
  return i < 0 ? [c || "Other", leaf] : [c.slice(0, i), leaf];
}

/** A dataset's or a map's group key: the topic key (schema 1.1, and every map),
 * else the concept's English top segment (pre-1.1). Translating the names must
 * not change which group an entry lands in. */
export const groupKey = (d: { topic?: unknown; concept?: unknown }): string => {
  const key = d.topic && typeof d.topic === "object" ? (d.topic as { key?: string }).key : "";
  return key || splitConcept(d.concept)[0];
};

export const isMapOnly = (m: MapItem): boolean => !(m.series || []).length;

/** Subhead of map-only entries on a maps list without `concept`. Sorts last. */
export const MAPS_LEAF = "\u0000maps";

/** The subhead a map-only entry sits under: its concept in the UI language,
 * which equals the catalog's leaf label where both have the subject. */
export const mapLeaf = (m: MapItem): string => label(m.concept) || MAPS_LEAF;

export type TopicRow = { kind: "dataset"; item: CatalogItem } | { kind: "map"; item: MapItem };
export interface TopicLeaf { leaf: string; rows: TopicRow[] }
export interface TopicGroup {
  key: string;
  leaves: TopicLeaf[];
  /** Datasets in the topic. */
  datasets: number;
  /** Maps whose topic this is, with or without `series`. */
  maps: number;
}

/** The Datasets tab: topics in `order` (keys absent from it follow, in first-seen
 * order), each with its leaves sorted by name (the catch-all Maps leaf last).
 * Within a leaf, datasets come first (canonical, then by title), then the
 * map-only entries by title. */
export function topicTree(catalog: CatalogItem[], maps: MapItem[], order: string[]): TopicGroup[] {
  const groups = new Map<string, Map<string, TopicRow[]>>();
  const add = (g: string, leaf: string, row: TopicRow) => {
    let leaves = groups.get(g);
    if (!leaves) groups.set(g, leaves = new Map());
    let rows = leaves.get(leaf);
    if (!rows) leaves.set(leaf, rows = []);
    rows.push(row);
  };
  for (const d of catalog) add(groupKey(d), splitConcept(d.concept)[1], { kind: "dataset", item: d });
  for (const m of maps) if (isMapOnly(m)) add(groupKey(m), mapLeaf(m), { kind: "map", item: m });

  const perTopic = mapsPerTopic(maps);
  const keys = [...order.filter(g => groups.has(g)), ...[...groups.keys()].filter(g => !order.includes(g))];
  const title = (r: TopicRow) => label(r.item.title);
  const canonical = (r: TopicRow) => (r.kind === "dataset" && r.item.canonical ? 1 : 0);
  return keys.map(key => {
    const leaves = groups.get(key)!;
    const sorted = [...leaves.keys()]
      .sort((a, b) => (a === MAPS_LEAF ? 1 : 0) - (b === MAPS_LEAF ? 1 : 0) || a.localeCompare(b))
      .map(leaf => ({
        leaf,
        rows: leaves.get(leaf)!.slice().sort((a, b) =>
          (a.kind === "map" ? 1 : 0) - (b.kind === "map" ? 1 : 0) ||
          canonical(b) - canonical(a) ||
          title(a).localeCompare(title(b))),
      }));
    const datasets = sorted.reduce((n, l) => n + l.rows.filter(r => r.kind === "dataset").length, 0);
    return { key, leaves: sorted, datasets, maps: perTopic[key] || 0 };
  });
}

/** Maps per topic key, counting those with `series` as well. */
export function mapsPerTopic(maps: MapItem[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const m of maps) { const k = groupKey(m); out[k] = (out[k] || 0) + 1; }
  return out;
}

/** A topic header's count: "7", "7 · 5 maps", or "1 map" for a topic only
 * maps use. */
export function topicCount(datasets: number, maps: number): string {
  if (!maps) return String(datasets);
  const m = maps === 1 ? tn("{n} map", 1) : tn("{n} maps", maps);
  return datasets ? `${datasets} · ${m}` : m;
}

/** The intro line above the tabs. Without maps it says what it always said. */
export function browseIntro(datasets: number, maps: number): string {
  return maps
    ? t("{n} datasets and {m} maps, by topic and provider.").replace("{n}", String(datasets)).replace("{m}", String(maps))
    : tn("All {n} datasets, by topic and provider", datasets);
}

// ---- one map ------------------------------------------------------------------

/** A source's short name in the UI language: the abbreviation its name ends in
 * ("FSO", "BFS", "FEDRO"), else its key in capitals. */
export function sourceShort(src: unknown): string {
  const m = /\(([^()]+)\)\s*$/.exec(srcOf(src) || "");
  if (m) return m[1] as string;
  const key = src && typeof src === "object" ? (src as { key?: string }).key : "";
  return (key || "").toUpperCase();
}

/** "2005–2026", or one year when the map covers one. */
export const mapSpan = (m: MapItem): string =>
  m.start && m.end && m.start !== m.end ? `${m.start}–${m.end}` : (m.start || m.end || "");

/** A map-only row's meta line: source · levels · span. */
export const mapRowMeta = (m: MapItem): string[] =>
  [sourceShort(m.source), label(m.levels_label), mapSpan(m)].filter(Boolean);

export interface MapCard {
  id: string;
  /** The map on maps.dataseries.org, without the language (main.ts adds it). */
  url: string;
  title: string;
  /** What the map shows, e.g. "Hectare grid · 100 m". */
  levels: string;
  /** The picture, or null for the placeholder. */
  thumbnail: string | null;
  /** "FSO · monthly · 2005–2026". An irregular frequency is left out. */
  meta: string;
  /** The first of the map's `series` that is in the catalog: the same statistic
   * as a chart. */
  chart: { id: string; title: string } | null;
}

export function mapCard(m: MapItem, catalog: CatalogItem[]): MapCard {
  const freq = m.frequency && m.frequency !== "irregular" ? freqLabel(m.frequency) : "";
  const ds = (m.series || []).map(id => catalog.find(c => c.id === id)).find(Boolean);
  return {
    id: m.id,
    url: m.url,
    title: label(m.title) || m.id,
    levels: label(m.levels_label),
    thumbnail: typeof m.thumbnail === "string" && m.thumbnail ? m.thumbnail : null,
    meta: [sourceShort(m.source), freq, mapSpan(m)].filter(Boolean).join(" · "),
    chart: ds ? { id: ds.id, title: label(ds.title) || ds.id } : null,
  };
}

/** The map for the landing's "Population map" chip, if the list has it. */
export const POPULATION_MAP = "ch_bfs_statpop_hectare";
export const findMap = (maps: MapItem[], id: string): MapItem | undefined => maps.find(m => m.id === id);
